import { computed, ref } from "vue"
import { defineStore } from "pinia"
import { UseRootStore } from "@/model/RootStore"
import { ExtendedAccessStatus, type ExtendedAccess } from "@/types/extendedAccess"
import * as apiClient from "@/utils/ApiClient"
import { analytics } from "@/utils/analytics"
import type { CtaSource } from "@/utils/ctaSource"

const MillisecondsPerDay = 86_400_000

export const useExtendedAccessStore = defineStore("extendedAccess", () => {
	const rootStore = UseRootStore()
	const extendedAccess = ref<ExtendedAccess | null>(null)
	const isStarting = ref(false)
	let loadedForToken: string | null = null

	const canStartExtendedAccess = computed(() => extendedAccess.value?.status === ExtendedAccessStatus.Available)
	const isActive = computed(() => extendedAccess.value?.status === ExtendedAccessStatus.Active)
	const daysLeft = computed(() => {
		const endsAt = extendedAccess.value?.endsAt
		return endsAt ? Math.max(Math.ceil((new Date(endsAt).getTime() - Date.now()) / MillisecondsPerDay), 0) : 0
	})

	async function load({ force = false }: { force?: boolean } = {}): Promise<void> {
		if (!rootStore.isUserLoggedIn) {
			extendedAccess.value = null
			loadedForToken = null
			return
		}
		if (!force && loadedForToken === rootStore.userApiToken) return

		loadedForToken = rootStore.userApiToken
		try {
			extendedAccess.value = await apiClient.fetchExtendedAccess(rootStore.userApiToken)
			analytics.userExtendedAccessIdentified(extendedAccess.value)
		} catch (error) {
			console.error("Failed to fetch extended access:", error)
			loadedForToken = null
		}
	}

	async function start(source: CtaSource): Promise<boolean> {
		isStarting.value = true
		try {
			await apiClient.updateCurrentUser(rootStore.userApiToken, { extendedAccess: true })
			analytics.extendedAccessStarted(source)
			await rootStore.refreshUserApiTokenIfNeeded({ force: true })
			await load({ force: true })
			return true
		} catch (error) {
			console.error("Failed to start extended access:", error)
			return false
		} finally {
			isStarting.value = false
		}
	}

	async function recordParcelReportDownload(): Promise<void> {
		if (!isActive.value) return
		try {
			await apiClient.recordParcelReportDownload(rootStore.userApiToken)
		} catch (error) {
			console.error("Failed to record parcel report download:", error)
		}
	}

	return {
		extendedAccess,
		isStarting,
		canStartExtendedAccess,
		isActive,
		daysLeft,
		load,
		start,
		recordParcelReportDownload,
	}
})
